import { faFolder, faPalette } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useMemo } from "react";

import { useNavigationStore } from "@/components/nav/state";
import { Icon } from "@/components/ui-v2/Icon/Icon";
import { CanvasIcon } from "@/components/ui-v2/icons";
import type { DriveAssetFragment } from "@/features/drive/queries.graphql";
import { InferenceParameters, ObjectType } from "@/types";
import { getObjectMetadata, getObjectType } from "@/utils/libraryObjectUtils";

import { SelectedObject } from "../types";
import { BlueprintInfo } from "./BlueprintInfo";
import { CreationInfo } from "./CreationInfo";
import { getFileAdvancedInfo, getFileBasicInfo } from "./file-mapper";
import { Info } from "./Info";
import { SectionTitle } from "./SectionTitle";
import { FileWithDetails } from "./types";

const Details = styled(Stack)(({ theme }) => ({
  background: theme.palette.secondary[6],
  border: `1px solid ${theme.palette.secondary[5]}`,
  borderRadius: "4px",
  padding: theme.spacing(2),
}));

interface SelectionSidebarObjectInfoProps {
  object: SelectedObject;
}

export const SelectionSidebarObjectInfo = (
  props: SelectionSidebarObjectInfoProps,
) => {
  const { object } = props;
  const { activeWorkspaceName } = useNavigationStore();

  const objectInfoWithSections = useMemo(() => {
    if (!object) {
      return [];
    }

    switch (object.__typename) {
      case "File":
        return renderFileDetails(object, activeWorkspaceName ?? "");
      case "Asset":
        return renderAssetDetails(object, activeWorkspaceName ?? "");
      case "Directory":
        return renderOtherObjectsDetails(
          "Directory",
          <FontAwesomeIcon icon={faFolder} />,
          object,
        );
      case "Style":
      case "ReferenceSet":
        return renderOtherObjectsDetails(
          "Model",
          <Icon icon={faPalette} />,
          object,
        );
      default:
        return [];
    }
  }, [object, activeWorkspaceName]);

  if (!object) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {objectInfoWithSections.length && (
        <Typography
          variant="micro"
          sx={{
            color: "secondary.2",
          }}
        >
          Details
        </Typography>
      )}
      <Details gap={3}>
        {objectInfoWithSections.map((section, i) =>
          section.map((objectInfo, j) => (
            <Stack spacing={2} key={`info-${i}-${j}`}>
              {j > 0 && <Divider />}
              {objectInfo}
            </Stack>
          )),
        )}
      </Details>
    </Stack>
  );
};

// TODO Avoid render functions in favor of React components `renderFileDetails(file)` => <FileDetails file={file} />`
const renderFileDetails = (
  file: FileWithDetails,
  workspaceName: string,
): JSX.Element[][] => {
  const basicInfoEntries = getFileBasicInfo(file);
  const advancedInfoEntries = getFileAdvancedInfo(file);

  const mainSection: JSX.Element[] = [
    <FileInferenceObjectType
      key="inference-mode"
      objectType={getObjectType(file)}
      parameters={file?.inferenceParameters ?? undefined}
    />,
    ...basicInfoEntries.map((entry) => <Info key={entry.id} entry={entry} />),
    ...(file.blueprint
      ? [
          <BlueprintInfo
            key="blueprintInfo"
            blueprintId={file.blueprint.id}
            name={file.blueprint.name}
            path={`/${workspaceName}/workflows/${file.blueprint.id}`}
          />,
        ]
      : []),
    <CreationInfo
      key="creationInfo"
      createdBy={file.createdByPrincipal ?? undefined}
      createdAt={file.createdAt}
    />,
  ];

  const details: JSX.Element[][] = [mainSection];

  if (advancedInfoEntries.length > 0) {
    details.push([
      <SectionTitle key="advanced-title" title="Advanced" />,
      ...advancedInfoEntries.map((entry) => (
        <Info key={entry.id} entry={entry} />
      )),
    ]);
  }

  return details;
};

const FileInferenceObjectType = (props: {
  objectType?: ObjectType;
  parameters?: InferenceParameters;
}) => {
  const { objectType, parameters } = props;

  const { icon, label } = getObjectMetadata(objectType, parameters);

  return (
    <Stack
      direction="row"
      sx={{
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 2,
      }}
    >
      {icon}
      <Typography>{label}</Typography>
    </Stack>
  );
};

const renderAssetDetails = (
  object: DriveAssetFragment,
  workspaceName: string,
): JSX.Element[][] => {
  return [
    [
      <Stack
        key="object-type"
        sx={{
          flexDirection: "row",
          gap: 2,
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <Typography>
          <CanvasIcon sx={{ width: 20, height: "auto" }} />
        </Typography>
        <Typography>Canvas Asset</Typography>
      </Stack>,
      <Info
        key="asset-dimensions"
        entry={{
          id: "asset-dimensions",
          label: "Dimensions",
          value: (
            <Typography
              variant="small"
              sx={{
                color: "secondary.200",
                fontWeight: 500,
              }}
            >
              {object.width} x {object.height}
            </Typography>
          ),
        }}
      ></Info>,
      ...(object.blueprint
        ? [
            <BlueprintInfo
              key="blueprintInfo"
              blueprintId={object.blueprint.id}
              name={object.blueprint.name}
              path={`/${workspaceName}/workflows/${object.blueprint.id}`}
            />,
          ]
        : []),
      <CreationInfo
        key="creationInfo"
        createdAt={object.createdAt}
        createdBy={object.createdByPrincipal ?? undefined}
      />,
    ],
  ];
};

const renderOtherObjectsDetails = (
  name: string,
  icon: JSX.Element,
  object: SelectedObject,
): JSX.Element[][] => {
  return [
    [
      <Stack
        key="object-type"
        sx={{
          flexDirection: "row",
          gap: 2,
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <Typography>{icon}</Typography>
        <Typography>{name}</Typography>
      </Stack>,
      <CreationInfo
        key="creationInfo"
        createdAt={object.createdAt}
        createdBy={object.createdByPrincipal ?? undefined}
      />,
    ],
  ];
};
